import { createPlatformRequestsAdapter, openInvite, openShare } from '@flobi/asset-library/share';
import type { InvitePayload, InviteSetup, SeatQuote, ShareAdapter, ShareState, ShareThing, Workspace } from '@flobi/asset-library/share';
import { gatewayCall } from './gateway';
import * as hub from './hub';

/**
 * Sharing in Artwork — the one place every Share button, menu row, shortcut and Invite
 * comes through (SHARING-MODEL.md §3.1–3.2). `<ArtworkShareHost/>` is mounted once;
 * these open it.
 *
 *   openFileShare({ id, name })   the editor's Share, Document ▸ Share…, ⇧⌘S, a card's ⋯
 *   openArtworkInvite()           the workspace menu's Invite people…
 *
 * A file's people ARE its project's people (decision 5); "Anyone with the link" is a
 * view page of the file as the renderer draws it (decision 3).
 */

export const fileThing = (file: { id: string; name?: string | null }): ShareThing => ({
  type: 'file',
  id: file.id,
  name: file.name || 'Untitled',
  noun: 'file',
  url: `${window.location.origin}/f/${file.id}`,
});

/** What Artwork adds to the popup's state (`artwork.share.get`). */
export interface ArtworkShareExtras {
  needsProject: boolean;
  projectId: string | null;
  projectName: string | null;
  canMove: boolean;
}

const call = <T>(command: string, thing: ShareThing, data: Record<string, unknown> = {}, opts?: { signal?: AbortSignal }) =>
  gatewayCall<T>('artwork', `artwork.share.${command}`, { type: thing.type, id: thing.id, ...data }, opts);

// The workspace each file lives in, from its last `get` — people search is scoped to it.
const workspaceOf = new Map<string, string>();

export const artworkShareAdapter: ShareAdapter = {
  async get(thing, opts) {
    const state = await call<ShareState & { artwork?: ArtworkShareExtras }>('get', thing, {}, opts);
    if (state?.workspace?.id) workspaceOf.set(thing.id, state.workspace.id);
    const a = state?.artwork;
    if (!a) return state;
    // In no project: "Move to project ›" — only for someone who may move it.
    if (a.needsProject) return a.canMove ? { ...state, inProject: false } : state;
    // A file's people ARE its project's people: People says so under "Via project".
    if (a.projectId && a.projectName && !state.inherited?.length) {
      return { ...state, inProject: true, inherited: [{ id: a.projectId, kind: 'project', name: a.projectName, count: state.people?.length ?? 0 }] };
    }
    return { ...state, inProject: true };
  },

  async searchPeople(thing, query, opts = {}) {
    const workspaceId = workspaceOf.get(thing.id);
    if (!workspaceId) return { results: [] };
    try {
      const res = await hub.searchPeople(workspaceId, query, opts);
      // Departments share through the project's department audience; teams don't.
      return { results: res.results.filter((r) => r.kind !== 'group' || String(r.id).startsWith('department:')) };
    } catch (err) {
      const name = (err as { name?: string })?.name;
      if (name === 'CanceledError' || name === 'AbortError') throw err;
      return { results: [] };
    }
  },

  add: (thing, input) => call('add', thing, { ...input }),
  setRole: (thing, principal, role) => call('setRole', thing, { principal, role }),
  remove: (thing, principal) => call('remove', thing, { principal }),
  setAccess: (thing, access) => call('setAccess', thing, { access: access.level, role: access.role }),
  link: (thing, input) => call('link', thing, { ...input }),

  requestAccess: (thing, input) =>
    hub.requestAccess({ app: 'artwork', resourceType: thing.type, resourceId: thing.id, role: input.role, ...(input.message ? { message: input.message } : {}), ...((input as { acceptContextId?: string }).acceptContextId ? { acceptContextId: (input as { acceptContextId?: string }).acceptContextId } : {}) }),

  listSharedWithMe: (input = {}) => hub.listSharedWithMe({ app: 'artwork', ...input }),

  // Who asked for access, answered in one click at the top of Share. The thing's type
  // ('file') is the resourceType `requestAccess` sends, so the default mapping is right.
  ...createPlatformRequestsAdapter({ request: gatewayCall, app: 'artwork' }),
};

/* ── a project's own Share (projects-back `projects.share.*`) ─────────────── */

const projectCall = <T>(command: string, thing: ShareThing, data: Record<string, unknown> = {}, opts?: { signal?: AbortSignal }) =>
  gatewayCall<T>('projects', `projects.share.${command}`, { type: thing.type, id: thing.id, ...data }, opts);

/** A project opened from Artwork's project page or card: the same Share the Projects app opens. */
export const projectShareAdapter: ShareAdapter = {
  async get(thing, opts) {
    const state = await projectCall<ShareState>('get', thing, {}, opts);
    if (state?.workspace?.id) workspaceOf.set(thing.id, state.workspace.id);
    return state;
  },
  searchPeople: (thing, query, opts) => artworkShareAdapter.searchPeople!(thing, query, opts),
  add: (thing, input) => projectCall('add', thing, { ...input }),
  setRole: (thing, principal, role) => projectCall('setRole', thing, { principal, role }),
  remove: (thing, principal) => projectCall('remove', thing, { principal }),
  setAccess: (thing, access) => projectCall('setAccess', thing, { access: access.level, role: access.role }),
  link: (thing, input) => projectCall('link', thing, { ...input }),
  requestAccess: (thing, input) =>
    hub.requestAccess({ app: 'projects', resourceType: thing.type, resourceId: thing.id, role: input.role, ...(input.message ? { message: input.message } : {}) }),
  listSharedWithMe: (input = {}) => hub.listSharedWithMe({ app: 'projects', ...input }),
  ...createPlatformRequestsAdapter({ request: gatewayCall, app: 'projects' }),
};

export function openProjectShare(project: { id: string; name?: string | null }): void {
  if (!project?.id) return;
  openShare({ thing: { type: 'project', id: project.id, name: project.name || 'Untitled Project', noun: 'project' }, adapter: projectShareAdapter });
}

/* ── a file link opened in the wrong workspace (SHARING-MODEL §3.3) ──────── */

/** One of this person's workspaces, with the Clerk organization that switches to it. */
export interface FileWorkspace extends Workspace {
  clerkOrgId: string | null;
}

/**
 * Which of this person's OTHER workspaces holds the file (`artwork.share.get` with `locate`).
 * Null when it is in this one, in one they are not in, or nowhere: the backend answers those
 * alike, and never says anything about the file itself.
 */
export async function locateFile(id: string): Promise<FileWorkspace | null> {
  const res = await gatewayCall<{ workspace?: Partial<FileWorkspace> | null }>('artwork', 'artwork.share.get', { type: 'file', id, locate: true });
  const ws = res?.workspace;
  if (!ws || typeof ws.id !== 'string' || !ws.id) return null;
  return { id: ws.id, name: typeof ws.name === 'string' ? ws.name : '', logoUrl: ws.logoUrl ?? null, clerkOrgId: typeof ws.clerkOrgId === 'string' && ws.clerkOrgId ? ws.clerkOrgId : null };
}

/* ── workspace invite (InviteSheet) ──────────────────────────────────────── */

/**
 * users-back `members.add` with flobi-home's body: an explicit `appConfig` naming every
 * app the workspace has (an empty one would buy a Pro seat on Flow), the seat as
 * `wantedSeat` (the gateway replaces `seatType` with the caller's own), the addresses as
 * `emails` (`email` is the gateway's). A seat to pay for is paid inside `members.add`:
 * its answer carries Stripe's checkout URL, which returns to this page.
 */
export const inviteMethods: Pick<ShareAdapter, 'getInviteSetup' | 'quoteSeats' | 'sendInvites'> = {
  async getInviteSetup(workspace?: Workspace): Promise<InviteSetup> {
    const setup = (await hub.inviteSetup(workspace?.id)) as unknown as InviteSetup;
    return { ...setup, workspace: setup?.workspace?.name ? setup.workspace : workspace };
  },

  async quoteSeats(workspace, { seatType, count }): Promise<SeatQuote> {
    const q = await gatewayCall<{ canAdd?: boolean; reason?: string; requiresPayment?: boolean; prorationCents?: number }>('users', 'members.quoteSeat', {
      ...(workspace?.id ? { organizationId: workspace.id } : {}),
      wantedSeat: seatType,
    });
    if (!q || typeof q !== 'object') return { canAdd: true };
    if (q.canAdd === false) return { canAdd: false, ...(q.reason ? { reason: q.reason } : {}) };
    const per = Number(q.prorationCents) || 0;
    const paying = Boolean(q.requiresPayment) && q.reason !== 'free_seat_available';
    return { canAdd: true, toBuy: paying ? count : 0, perSeatCents: per, totalCents: paying ? per * count : 0 };
  },

  async sendInvites(workspace, input: InvitePayload) {
    const orgId = workspace?.id || null;
    const seats = await gatewayCall<{ apps?: { appSlug?: string }[] } | { appSlug?: string }[]>('users', 'members.getAppSeatsInfo', orgId ? { organizationId: orgId } : {});
    const apps = (Array.isArray(seats) ? seats : (seats?.apps ?? [])).filter((a) => a && typeof a.appSlug === 'string' && a.appSlug);
    if (!apps.length) {
      const err = new Error('No apps') as Error & { userMessage?: string };
      err.userMessage = "Couldn't read this workspace's apps, so nothing was sent. Try again.";
      throw err;
    }
    const tier = input.seatType === 'reviewer' ? 'guest' : 'free';
    const res = await gatewayCall<{ success?: boolean; requiresPayment?: boolean; checkoutUrl?: string; added?: { email?: string; status?: string; error?: string; blockedReason?: string }[] }>('users', 'members.add', {
      ...(orgId ? { organizationId: orgId } : {}),
      emails: input.emails,
      role: input.role,
      appConfig: Object.fromEntries(apps.map((a) => [a.appSlug as string, tier])),
      wantedSeat: input.seatType,
      ...(input.requestId ? { requestId: input.requestId } : {}),
      returnUrl: window.location.href,
      ...(input.departmentId ? { departmentId: input.departmentId } : {}),
      ...(input.teamId ? { teamId: input.teamId } : {}),
      ...(input.projectId ? { projectId: input.projectId, projectRole: 'member' } : {}),
    });
    if (res?.checkoutUrl && res.requiresPayment) {
      window.location.assign(res.checkoutUrl);
      return { sent: input.emails };
    }
    const rows = Array.isArray(res?.added) ? res.added : [];
    const sent: string[] = [];
    const failed: { email: string; reason?: string }[] = [];
    for (const email of input.emails) {
      const row = rows.find((r) => String(r?.email ?? '').toLowerCase() === email.toLowerCase());
      if (!row && res?.success !== false) sent.push(email);
      else if (row && ['invited', 'added', 'awaiting_payment'].includes(String(row.status))) sent.push(email);
      else failed.push({ email, reason: row?.error || row?.blockedReason || "Couldn't send." });
    }
    return { sent, failed };
  },
};

/* ── openers ─────────────────────────────────────────────────────────────── */

/** `section: 'requests'` opens on who asked for access (an access-request notification's link). */
export function openFileShare(file: { id: string; name?: string | null }, opts: { section?: 'requests' } = {}): void {
  if (!file?.id) return;
  openShare({ thing: fileThing(file), adapter: artworkShareAdapter, ...(opts.section ? { section: opts.section } : {}) });
}

/** Invite people into the workspace — Flobi's one invite sheet, opened in place. */
export function openArtworkInvite(): void {
  openInvite({ adapter: { ...artworkShareAdapter, ...inviteMethods } });
}

/* ── the public view page (no account; the token is the credential) ──────── */

export type LinkPictures =
  | { status: 'ready'; drawnAt: string; pages: { name: string; width: number; height: number; url: string | null; pageId?: string; artboardId?: string }[]; newerComing: boolean }
  | { status: 'drawing'; progress: number }
  | { status: 'failed'; message: string };

export interface LinkView {
  name: string;
  allowDownload: boolean;
  /** 'comment': a Can-comment link — the page shows the file's comments and takes new ones (W5-AR2). */
  role?: 'view' | 'comment';
  pictures: LinkPictures;
}

export const viewLink = (token: string, password?: string) =>
  gatewayCall<LinkView>('artwork', 'artwork.link.open', { token, ...(password ? { password } : {}) });

/* ── comments on the view page (a Can-comment link, W5-AR2) ─────────────── */

export const linkComments = (token: string, password?: string) =>
  gatewayCall<{ threads: import('../services/comments').CommentThread[]; signedIn: boolean }>('artwork', 'artwork.link.comments', { token, ...(password ? { password } : {}) });

export const linkComment = (token: string, password: string | undefined, anchor: import('../services/comments').CommentAnchor, body: string) =>
  gatewayCall<import('../services/comments').CommentThread>('artwork', 'artwork.link.comment', { token, ...(password ? { password } : {}), anchor, body });
